import { useState, useEffect, useRef, useMemo } from "react";
import {
  escapeCssFontFamily,
  getCanonicalGoogleFontFamily,
  getCssFontFormat,
} from "./legacyDesignerFonts";

// Font type definitions
export type FontType = "google" | "system" | "branding";

export interface BrandingFontLink {
  link: string;
  format: string;
}

export interface BrandingFont {
  name: string;
  value: string;
  links: BrandingFontLink[];
  type: string;
}

export interface DefaultFont {
  id: number;
  name: string;
  value: string;
  type: "google" | "system";
  createdAt: string;
  updatedAt: string | null;
  links?: BrandingFontLink[];
}

export interface BrandingFonts {
  branding?: BrandingFont[];
  default?: DefaultFont[];
}

export interface FontLoaderProps {
  fontNames: string[];
  fontWeights?: { [fontName: string]: number[] };
  brandingFonts?: BrandingFonts;
}

const matchesFont = (
  font: { name: string; value: string },
  fontName: string,
) => {
  const normalizedName = fontName.trim().toLowerCase();
  return [font.name, font.value].some(
    (name) => name?.trim().toLowerCase() === normalizedName,
  );
};

const detectFontType = (
  fontName: string,
  brandingFonts?: BrandingFonts
): FontType => {
  if (!fontName || typeof fontName !== "string") {
    return "system";
  }

  if (
    brandingFonts?.branding?.some(
      (font) => matchesFont(font, fontName)
    )
  ) {
    return "branding";
  }

  const defaultFont = brandingFonts?.default?.find(
    (font) => matchesFont(font, fontName)
  );
  if (defaultFont) {
    if (
      defaultFont.type === "system" &&
      defaultFont.links &&
      defaultFont.links.length > 0
    ) {
      return "branding";
    }
    return defaultFont.type;
  }

  return "google";
};

const getFontsByType = (fontNames: string[], brandingFonts?: BrandingFonts) => {
  const fontsByType: Record<FontType, string[]> = {
    google: [],
    system: [],
    branding: [],
  };

  fontNames.forEach((fontName) => {
    if (!fontName || typeof fontName !== "string") {
      return;
    }

    const fontType = detectFontType(fontName, brandingFonts);
    fontsByType[fontType].push(fontName);
  });

  return fontsByType;
};

export const useFontLoader = ({
  fontNames,
  fontWeights = {},
  brandingFonts,
}: FontLoaderProps) => {
  const [fontLinks, setFontLinks] = useState<HTMLLinkElement[]>([]);
  const [styleElements, setStyleElements] = useState<HTMLStyleElement[]>([]);
  const elementsRef = useRef<{
    links: HTMLLinkElement[];
    styles: HTMLStyleElement[];
  }>({ links: [], styles: [] });

  const memoizedFontNames = useMemo(() => {
    if (!Array.isArray(fontNames)) {
      return [];
    }
    const validFontNames = fontNames.filter(
      (fontName) =>
        fontName !== undefined && fontName !== null && fontName !== ""
    );
    // Remove duplicates - keep only unique font names
    return Array.from(new Set(validFontNames));
  }, [fontNames]);

  const memoizedBrandingFonts = useMemo(() => {
    return brandingFonts;
  }, [brandingFonts]);

  useEffect(() => {
    if (memoizedFontNames.length === 0) {
      return;
    }

    const createFontFaceRule = (
      fontName: string,
      fontLinks: BrandingFontLink[],
      fontStyle = "normal"
    ) => {
      const fileLinks = fontLinks.filter(
        (link) => !link.link.includes("fonts.googleapis.com")
      );

      if (fileLinks.length === 0) {
        return "";
      }

      const srcUrls = fileLinks
        .map(
          (link) =>
            `url('${link.link}') format('${getCssFontFormat(link.link, link.format)}')`
        )
        .join(", ");

      return `@font-face {
    font-family: '${escapeCssFontFamily(fontName)}';
    font-style: ${fontStyle};
    font-display: swap;
    src: ${srcUrls};
  }`;
    };

    const loadGoogleFonts = (googleFonts: string[]) => {
      const links: HTMLLinkElement[] = [];

      googleFonts.forEach((fontName) => {
        if (!fontName || typeof fontName !== "string") {
          return;
        }

        const apiFont = brandingFonts?.default?.find(
          (font) => matchesFont(font, fontName)
        );
        const cssLink = apiFont?.links?.find((link) =>
          link.link.includes("fonts.googleapis.com")
        );
        const familyName = getCanonicalGoogleFontFamily(apiFont?.name || fontName);

        // Google 400s on any axis the family does not publish, so only ask for
        // weights we were actually told about. A bare `family=Name` is valid
        // for every family and returns its default weight.
        const weights = fontWeights[fontName] ?? [];
        const weightsString =
          weights.length > 0
            ? `:wght@${Array.from(new Set(weights))
                .sort((a, b) => a - b)
                .join(";")}`
            : "";

        const link = document.createElement("link");
        link.href =
          cssLink?.link ??
          `https://fonts.googleapis.com/css2?family=${encodeURIComponent(familyName).replace(
            /%20/g,
            "+"
          )}${weightsString}&display=swap`;
        link.rel = "stylesheet";
        document.head.appendChild(link);

        links.push(link);
      });

      return links;
    };

    const loadBrandingFonts = (brandingFontNames: string[]) => {
      const styleElement = document.createElement("style");
      const links: HTMLLinkElement[] = [];
      document.head.appendChild(styleElement);

      brandingFontNames.forEach((fontName) => {
        if (!fontName || typeof fontName !== "string") {
          return;
        }

        const brandingFont = brandingFonts?.branding?.find(
          (font) => matchesFont(font, fontName)
        );

        const defaultFont = brandingFonts?.default?.find(
          (font) => matchesFont(font, fontName)
        );

        const fontToUse = brandingFont || defaultFont;

        if (fontToUse && fontToUse.links && fontToUse.links.length > 0) {
          const cssLinks = fontToUse.links.filter((link) =>
            link.link.includes("fonts.googleapis.com")
          );
          cssLinks.forEach((fontLink) => {
            const link = document.createElement("link");
            link.href = fontLink.link;
            link.rel = "stylesheet";
            document.head.appendChild(link);
            links.push(link);
          });

          let fontStyle = "normal";

          if (brandingFont && brandingFont.type) {
            if (
              brandingFont.type === "italic" ||
              brandingFont.type === "bold italic" ||
              brandingFont.type === "italic bold"
            ) {
              fontStyle = "italic";
            }
          }

          const fontFaceRule = createFontFaceRule(
            fontToUse.name,
            fontToUse.links,
            fontStyle
          );

          if (fontFaceRule) {
            styleElement.appendChild(document.createTextNode(fontFaceRule));
          }
        }
      });

      return { styleElement, links };
    };

    const fontsByType = getFontsByType(
      memoizedFontNames,
      memoizedBrandingFonts
    );

    const newLinks: HTMLLinkElement[] = [];
    const newStyleElements: HTMLStyleElement[] = [];

    if (fontsByType.google.length > 0) {
      const googleLinks = loadGoogleFonts(fontsByType.google);
      newLinks.push(...googleLinks);
    }

    if (fontsByType.branding.length > 0) {
      const brandingFonts = loadBrandingFonts(fontsByType.branding);
      newLinks.push(...brandingFonts.links);
      newStyleElements.push(brandingFonts.styleElement);
    }

    elementsRef.current = { links: newLinks, styles: newStyleElements };

    setFontLinks(newLinks);
    setStyleElements(newStyleElements);

    return () => {
      elementsRef.current.links.forEach((link) => {
        if (link && link.parentNode) {
          link.parentNode.removeChild(link);
        }
      });

      elementsRef.current.styles.forEach((styleElement) => {
        if (styleElement && styleElement.parentNode) {
          styleElement.parentNode.removeChild(styleElement);
        }
      });
    };
  }, [
    memoizedFontNames,
    memoizedBrandingFonts,
    fontWeights,
    brandingFonts?.branding,
    brandingFonts?.default,
  ]);

  return { fontLinks, styleElements };
};
